import { useStoreActions, useStoreState } from 'easy-peasy';
import { useMemo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { Config } from '@ge/shared/data-hooks';

import * as appServices from '../services/app';

const APP_DATA_REFRESH_INTERVAL = 1000 * 60 * 60 * 24;

export const useSiteName = ({ siteId }) => {
  const getSiteById = useStoreState((state) => state.sites.getSiteById);
  const site = getSiteById(siteId);
  return site?.name ?? '';
};

export const useTurbineTypes = () => {
  const { data = null, ...queryRest } = useQuery(
    ['useTurbineTypes'],
    async () => {
      const { result } = await appServices.getTurbineTypes({});
      return result.data;
    },
    { ...Config.EXECUTE_ONCE, staleTime: APP_DATA_REFRESH_INTERVAL },
  );
  const { setTurbineTypes } = useStoreActions((actions) => actions.inspectionsFeature);
  useEffect(() => {
    if (data !== null) setTurbineTypes(data);
  }, [data, setTurbineTypes]);
  return {
    data,
    ...queryRest,
  };
};

export const useTurbineTypeById = ({ turbineId }) => {
  const [turbineType, setTurbineType] = useState(null);
  const { data: turbineTypes = [] } = useTurbineTypes();
  useEffect(() => {
    if (turbineTypes) {
      const _turbineType = turbineTypes.find((t) => t.id == turbineId);
      setTurbineType(_turbineType);
    }
  }, [turbineTypes, turbineId]);
  return turbineType;
};

export const useTurbineDetailsList = ({ siteId }) => {
  const {
    data = null,
    error,
    isLoading,
  } = useQuery(
    ['useTurbineDetailsList', siteId],
    async () => {
      if (siteId === undefined || siteId === null) return null;
      const { result } = await appServices.getTurbineDetailForSiteId(siteId, {});
      return result;
    },
    { ...Config.EXECUTE_ONCE, staleTime: APP_DATA_REFRESH_INTERVAL },
  );
  return useMemo(
    () => ({
      data,
      error,
      isLoading,
    }),
    [data, error, isLoading],
  );
};

export const useComponentDetailsList = ({ assetId }) => {
  const {
    data = null,
    error,
    isLoading,
  } = useQuery(
    ['useComponentDetailsList', assetId],
    async () => {
      if (assetId === undefined || assetId === null) return null;
      const { result } = await appServices.getComponentDetailList(assetId, {});
      return result;
    },
    { ...Config.EXECUTE_ONCE, staleTime: APP_DATA_REFRESH_INTERVAL },
  );
  return useMemo(
    () => ({
      data,
      error,
      isLoading,
    }),
    [data, error, isLoading],
  );
};
